import React from 'react';
import { motion } from 'framer-motion';
import { ListChecks, Loader2, AlertTriangle, ArrowRight, RotateCcw, X, HelpCircle, Sparkles } from 'lucide-react';
import { pageLabel } from '@/lib/sitePages';

// ─── Plan mode, in the chat ───────────────────────────────────────────────────
// The chat's side of plan mode. For a new site the plan itself fills the
// workbench (PlanBlueprint); the chat says where it is and keeps Build in
// reach. For a change to an existing site the plan IS the chat card: which
// pages change and how, approved before a credit is spent.

const SIZE_LABEL = { small: 'Small change', medium: 'Medium change', large: 'Large change' };

function Bubble({ children, tone = 'default' }) {
  const border = tone === 'warn' ? 'border-amber-500/25 bg-amber-500/[0.06]' : 'border-zinc-800 bg-zinc-900';
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-2.5 min-w-0">
      <div className="w-7 h-7 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0 mt-1">
        <ListChecks className="w-3.5 h-3.5 text-orange-400" />
      </div>
      <div className={`flex-1 min-w-0 rounded-2xl rounded-bl-sm border px-3.5 py-3 ${border}`}>{children}</div>
    </motion.div>
  );
}

/** A new site's plan: where it is, and the way to build it. */
export function PlanChatCard({ status, plan, error, onBuild, onRetry, onSkip }) {
  if (status === 'drafting') {
    return (
      <Bubble>
        <p className="flex items-center gap-2 text-[13px] text-zinc-300"><Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" /> Drafting a plan for your site…</p>
        <p className="mt-1 text-[11px] text-zinc-500">Free. Nothing is built until you say so.</p>
      </Bubble>
    );
  }
  if (status === 'error' && !plan) {
    return (
      <Bubble tone="warn">
        <p className="flex items-center gap-2 text-[13px] font-medium text-amber-300"><AlertTriangle className="w-3.5 h-3.5" /> The plan could not be drafted</p>
        <p className="mt-1 text-[11.5px] text-zinc-400 break-words">{error} No credit was used.</p>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          <button onClick={onRetry} className="h-7 px-3 rounded-full bg-orange-500 hover:bg-orange-400 text-ink text-[11.5px] font-semibold inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Try again</button>
          <button onClick={onSkip} className="h-7 px-3 rounded-full border border-zinc-700 text-[11.5px] font-semibold text-zinc-300 hover:text-white">Build without a plan</button>
        </div>
      </Bubble>
    );
  }
  if (!plan) return null;
  const pages = plan.pages || [];
  return (
    <Bubble>
      <p className="text-[13px] text-zinc-200 leading-relaxed">
        {status === 'revising' ? 'Updating the plan…' : 'Here is the plan.'}{' '}
        <span className="text-white font-medium">{pages.length} page{pages.length === 1 ? '' : 's'}</span>:{' '}
        {pages.map(p => p.label || pageLabel(p.path)).join(', ')}.
      </p>
      <p className="mt-1.5 text-[11.5px] text-zinc-500 leading-relaxed">
        Review and change it in the plan view, or tell me here what to change — it is free.
      </p>
      <button onClick={() => onBuild(plan)} disabled={status === 'revising'}
        className="mt-2.5 h-7 px-3 rounded-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-ink text-[11.5px] font-semibold inline-flex items-center gap-1">
        Build this site <ArrowRight className="w-3 h-3" />
      </button>
    </Bubble>
  );
}

/** A planned change to an existing site, waiting to be applied. */
export function EditPlanCard({ editPlan, onApply, onDiscard, onRetry, credits }) {
  const { status, plan, error, request } = editPlan;

  if (status === 'drafting' || status === 'revising') {
    return (
      <Bubble>
        <p className="flex items-center gap-2 text-[13px] text-zinc-300">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" /> {status === 'revising' ? 'Updating the plan…' : 'Planning your change…'}
        </p>
        <p className="mt-1 text-[11px] text-zinc-500">Free — nothing changes until you apply it.</p>
      </Bubble>
    );
  }
  if (status === 'error' && !plan) {
    return (
      <Bubble tone="warn">
        <p className="flex items-center gap-2 text-[13px] font-medium text-amber-300"><AlertTriangle className="w-3.5 h-3.5" /> Could not plan that change</p>
        <p className="mt-1 text-[11.5px] text-zinc-400 break-words">{error}</p>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          <button onClick={onRetry} className="h-7 px-3 rounded-full bg-orange-500 hover:bg-orange-400 text-ink text-[11.5px] font-semibold inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Try again</button>
          <button onClick={onDiscard} className="h-7 px-3 rounded-full border border-zinc-700 text-[11.5px] font-semibold text-zinc-300 hover:text-white">Discard</button>
        </div>
      </Bubble>
    );
  }
  if (!plan) return null;

  return (
    <Bubble>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-orange-300">Planned change</p>
        <span className="text-[10px] rounded-full border border-zinc-700 px-2 py-0.5 text-zinc-400">{SIZE_LABEL[plan.size] || 'Change'}</span>
      </div>
      {plan.summary && <p className="mt-1.5 text-[13px] text-white leading-relaxed break-words">{plan.summary}</p>}
      <ol className="mt-2 space-y-1.5">
        {plan.steps.map((s, i) => (
          <li key={i} className="flex items-start gap-2 text-[12px] leading-snug">
            <span className={`shrink-0 mt-px rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${s.isNew ? 'bg-emerald-500/15 text-emerald-300' : 'bg-zinc-800 text-zinc-400'}`}>
              {s.page === 'all pages' ? 'All pages' : `${s.isNew ? 'New · ' : ''}${pageLabel(s.page)}`}
            </span>
            <span className="text-zinc-300 break-words min-w-0">{s.change}</span>
          </li>
        ))}
      </ol>
      {plan.questions?.length > 0 && (
        <div className="mt-2.5 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-2.5 py-2">
          {plan.questions.map((q, i) => (
            <p key={i} className="flex items-start gap-1.5 text-[11.5px] text-amber-200 leading-snug"><HelpCircle className="w-3.5 h-3.5 shrink-0 mt-px" /> {q}</p>
          ))}
          <p className="mt-1 text-[10.5px] text-zinc-500">Answer below and the plan is updated — still free.</p>
        </div>
      )}
      {status === 'error' && error && <p className="mt-2 text-[11px] text-amber-300">{error}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <button onClick={onApply} className="h-7 px-3 rounded-full bg-orange-500 hover:bg-orange-400 text-ink text-[11.5px] font-semibold inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Apply · 1 credit
        </button>
        <button onClick={onDiscard} className="h-7 px-2.5 rounded-full text-[11.5px] font-semibold text-zinc-400 hover:text-white inline-flex items-center gap-1">
          <X className="w-3 h-3" /> Discard
        </button>
      </div>
      <p className="mt-2 text-[10.5px] text-zinc-500">
        Not quite right? Say what to change below.{typeof credits === 'number' ? ` ${credits} credit${credits === 1 ? '' : 's'} left.` : ''}
      </p>
      {request && <p className="sr-only">For: {request}</p>}
    </Bubble>
  );
}
